import { FiImage, FiCheck, FiClock, FiTrash2, FiSend, FiLink, FiX } from 'react-icons/fi';
import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import MediaPickerModal from '../../components/common/MediaPickerModal';
import { platformService } from '../../services/platformIntegrations';
import { useNavigate } from 'react-router-dom';
import { publishSocialPost, saveSocialPosts } from '../../api/social';
import useSocialPlatforms from '../../hooks/useSocialPlatforms';


const QuickPostModal = ({ isOpen, onClose, initialData = null }) => {
  const { activeWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [eventId, setEventId] = useState(null);
  const [eventType, setEventType] = useState('campaign');
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [media, setMedia] = useState(null);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
  const [connectedPlatformIds, setConnectedPlatformIds] = useState([]);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const { platforms, isLoading: platformsLoading } = useSocialPlatforms();

  useEffect(() => {
    if (isOpen && activeWorkspace) {
      fetchConnections();
      if (initialData) {
         if (initialData.event_date) setScheduledDate(initialData.event_date);
         if (initialData.title) setTitle(initialData.title);
         if (initialData.description) setContent(initialData.description);
         if (initialData.location) setLocation(initialData.location);
         if (initialData.type) setEventType(initialData.type);
         if (initialData.id) setEventId(initialData.id);
         if (initialData.platforms) setSelectedPlatforms(initialData.platforms);
         else setSelectedPlatforms([]);
         setMedia(null);
      } else {
          setContent('');
          setTitle('');
          setLocation('');
          setEventType('campaign');
          setEventId(null);
          setScheduledDate('');
          setSelectedPlatforms([]);
          setMedia(null);
      }
    }
  }, [isOpen, activeWorkspace, initialData]);

  const fetchConnections = async () => {
    setIsLoadingConnections(true);
    try {
      const connections = await platformService.getConnections(activeWorkspace.id);
      // Store the IDs of platforms that are connected
      setConnectedPlatformIds(connections.map(c => c.platform_id));
    } catch (error) {
      console.error('Error fetching connections:', error);
    } finally {
      setIsLoadingConnections(false);
    }
  };

  if (!isOpen) return null;

  const togglePlatform = (platformId, originalId) => {
    // Check connection first
    const isConnected = connectedPlatformIds.includes(originalId);

    if (!isConnected) {
      if (window.confirm(`You are not connected to ${platforms.find(p => p.id === platformId)?.name}. Would you like to connect now?`)) {
        onClose();
        navigate('/connect-apps');
      }
      return;
    }

    setSelectedPlatforms(prev => {
      if (prev.includes(platformId) || prev.includes(originalId)) {
        return prev.filter(p => p !== platformId && p !== originalId);
      } else {
        return [...prev, platformId];
      }
    });
  };

  const handleDelete = async () => {
    if (!eventId) return;
    if (window.confirm('Are you sure you want to delete this event?')) {
      setIsSubmitting(true);
      try {
        const { error } = await supabase.from('marketing_events').delete().eq('id', eventId);
        if (error) throw error;
        toast.success('Event deleted successfully');
        onClose();
        queryClient.invalidateQueries({ queryKey: ['socialMediaPosts'] });
        queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', activeWorkspace.id] });
      } catch (err) {
        console.error('Error deleting event:', err);
        toast.error('Failed to delete event: ' + err.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  };


  const getCharLimit = () => {
    let limit = 5000;
    if (selectedPlatforms.length === 0) return limit;

    for (const p of selectedPlatforms) {
        if (p === 'twitter' || p === 'twitter_social') {
            limit = Math.min(limit, 280);
        } else if (p.includes('linkedin')) {
            limit = Math.min(limit, 2500);
        } else if (p.includes('facebook')) {
            limit = Math.min(limit, 63206);
        } else if (p.includes('tiktok')) {
            limit = Math.min(limit, 2200);
        }
    }
    return limit;
  };

  const charLimit = getCharLimit();
  const remainingChars = charLimit - (content ? content.length : 0);
  const isOverLimit = remainingChars < 0;

  const checkCharLimit = () => {
      if (content && content.length > charLimit) {
          return window.confirm(`Your post is ${content.length} characters, but the limit is ${charLimit} for one or more selected platforms. It will be truncated. Do you want to continue?`);
      }
      return true;
  };

  const handleSubmit = async (status) => {

    if (!content.trim()) {
      toast.error('Please enter post content');
      return;
    }

    if (selectedPlatforms.length > 0 && !checkCharLimit()) {
        return;
    }
    // Removed strict platform check to allow saving calendar events without platforms
    if (status === 'scheduled' && !scheduledDate) {
        toast.error('Please select a date/time for scheduled post');
        return;
    }

    if (!activeWorkspace) {
        toast.error('No active workspace found');
        return;
    }

    setIsSubmitting(true);

    try {
      const scheduledAt = status === 'published'
        ? new Date().toISOString()
        : new Date(scheduledDate).toISOString();


      // Save social posts if platforms are selected
      if (selectedPlatforms.length > 0) {
          const postsToInsert = selectedPlatforms.map(platform => ({
            workspace_id: activeWorkspace.id,
            title: title || `Quick Post for ${platform}`,
            content: content,
            status: status,
            platform: platform,
            author: 'User',
            avatar: '/gnome-tech_logo.png',
            scheduled_at: scheduledAt,
            image: media?.image_url || null,
            media_type: media?.type || 'image',
          }));

          let publishErrors = [];

          if (status === 'published') {
            for (const post of postsToInsert) {
              try {
                await publishSocialPost(post);
              } catch (err) {
                publishErrors.push(`${post.platform}: ${err.message}`);
              }
            }
          }

          await saveSocialPosts(postsToInsert);

          if (publishErrors.length > 0) {
              if (publishErrors.length === selectedPlatforms.length) {
                  throw new Error(`Failed to publish to all selected platforms:\n${publishErrors.join('\n')}`);
              } else {
                  toast.error(`Partially succeeded. Failed to publish to:\n${publishErrors.join('\n')}`);
              }
          }
      }

      // Save calendar event
      if (title || selectedPlatforms.length === 0) {
          if (eventId) {
              const { error: eventError } = await supabase.from('marketing_events').update({
                  title: title || 'Quick Event',
                  description: content,
                  event_date: scheduledAt,
                  type: eventType,
                  location: location
              }).eq('id', eventId);
              if (eventError) console.error("Failed to update calendar event", eventError);
          } else {
              const { error: eventError } = await supabase.from('marketing_events').insert([{
                  workspace_id: activeWorkspace.id,
                  title: title || 'Quick Event',
                  description: content,
                  event_date: scheduledAt,
                  type: eventType,
                  location: location
              }]);
          }
          }

      toast.success(status === 'published' ? 'Post published successfully!' : 'Post scheduled successfully!');

      // Reset form and close
      setContent('');
      setSelectedPlatforms([]);
      setScheduledDate('');
      setMedia(null);
      onClose();

      // Refresh posts list
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts'] });
      queryClient.invalidateQueries({ queryKey: ['socialMediaPosts', activeWorkspace.id] });

    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-charcoal-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header - Added flex-shrink-0 to prevent compression */}
        <div className="flex-shrink-0 p-4 border-b border-charcoal-100 dark:border-charcoal-700 flex justify-between items-center bg-charcoal-50 dark:bg-charcoal-800">
          <h3 className="font-bold text-lg text-charcoal-900 dark:text-white flex items-center gap-2">
            <SafeIcon icon={FiSend} className="text-charcoal-900 dark:text-white" />
            Quick Post
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-charcoal-200 dark:hover:bg-charcoal-700 rounded-full text-charcoal-500 dark:text-charcoal-400 transition-colors">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1 min-h-0">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">Select Platforms</label>
            {platformsLoading ? (
                <div className="text-sm text-charcoal-500 dark:text-charcoal-400">Loading platforms...</div>
            ) : (
                <div className="flex flex-wrap gap-3">
                  {platforms.map((platform) => {
                    const isSelected = selectedPlatforms.includes(platform.id) || selectedPlatforms.includes(platform.original_id);
                    const isConnected = connectedPlatformIds.includes(platform.original_id);

                    return (
                      <button
                        key={platform.id}
                        onClick={() => togglePlatform(platform.id, platform.original_id)}
                        disabled={platform.status === 'coming_soon' || platform.status === 'maintenance'}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-all ${
                          isSelected
                            ? 'border-charcoal-900 dark:border-white bg-charcoal-50 dark:bg-charcoal-800 ring-1 ring-charcoal-900 dark:ring-white'
                            : isConnected
                              ? 'border-charcoal-200 dark:border-charcoal-700 hover:border-charcoal-300 dark:hover:border-charcoal-500'
                              : 'border-charcoal-200 dark:border-charcoal-700 bg-charcoal-50 dark:bg-charcoal-800 opacity-70 hover:opacity-100 hover:border-charcoal-300 dark:hover:border-charcoal-500'
                        } ${(platform.status === 'coming_soon' || platform.status === 'maintenance') ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={!isConnected ? "Not connected - click to connect" : ""}
                      >
                        <div className={`w-6 h-6 ${platform.color} rounded flex items-center justify-center relative`}>
                          <SafeIcon icon={platform.icon} className="text-white text-xs" />
                          {!isConnected && !isLoadingConnections && (
                            <div className="absolute -top-1 -right-1 bg-white dark:bg-charcoal-800 rounded-full p-0.5 shadow-sm">
                               <SafeIcon icon={FiLink} className="text-charcoal-400 dark:text-charcoal-500 text-[8px]" />
                            </div>
                          )}
                        </div>
                        <span className={`font-medium text-sm ${isConnected ? 'text-charcoal-700 dark:text-charcoal-200' : 'text-charcoal-500 dark:text-charcoal-500'}`}>
                          {platform.name}
                        </span>
                        {isSelected && <SafeIcon icon={FiCheck} className="text-charcoal-900 dark:text-white text-xs ml-1" />}
                      </button>
                    );
                  })}
                </div>
            )}
          </div>

          {/* Event Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                  <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">Location (Optional)</label>
                  <input
                      type="text"
                      className="w-full p-2 border border-charcoal-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white rounded-lg focus:ring-2 focus:ring-charcoal-900 dark:focus:ring-white focus:border-transparent"
                      placeholder="Event Location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                  />
              </div>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">Post Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What do you want to share?"
              className="w-full p-4 border border-charcoal-200 dark:border-charcoal-700 rounded-lg focus:ring-2 focus:ring-charcoal-900 dark:focus:ring-white focus:border-transparent min-h-[150px] resize-none bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white"
            ></textarea>
            <div className={`text-xs mt-1 flex flex-col gap-1 ${isOverLimit ? 'text-red-500 font-bold' : remainingChars < 100 ? 'text-orange-500 font-semibold' : 'text-charcoal-500 dark:text-charcoal-400'}`}>
              <div>
                <span className="text-sm">{content?.length || 0}</span> / {charLimit}
              </div>
              {isOverLimit && (
                <span className="warning block mt-1">
                  ⚠️ Post exceeds API limit by {Math.abs(remainingChars)} characters
                </span>
              )}
              {!isOverLimit && remainingChars < 100 && remainingChars >= 0 && (
                <span className="caution block mt-1">
                  ⚡ Approaching character limit
                </span>
              )}
            </div>
          </div>

          {/* Media Attachment */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">Media</label>
            {media ? (
                <div className="relative inline-block group">
                    {media.type === 'video' ? (
                        <video
                            src={media.image_url}
                            className="h-32 w-auto rounded-lg border border-charcoal-200 dark:border-charcoal-700 object-cover"
                            controls
                        />
                    ) : (
                        <img
                            src={media.image_url}
                            alt="Selected media"
                            className="h-32 w-auto rounded-lg border border-charcoal-200 dark:border-charcoal-700 object-cover"
                        />
                    )}
                    <button
                        onClick={() => setMedia(null)}
                        className="absolute -top-2 -right-2 bg-white dark:bg-charcoal-800 text-red-500 rounded-full p-1 shadow-md hover:bg-red-50 dark:hover:bg-red-900/20 border border-charcoal-100 dark:border-charcoal-700 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove media"
                    >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <button
                        onClick={() => setIsMediaPickerOpen(true)}
                        className="w-fit flex items-center gap-2 px-4 py-2 border border-charcoal-200 dark:border-charcoal-700 rounded-lg text-sm text-charcoal-600 dark:text-charcoal-300 hover:bg-charcoal-50 dark:hover:bg-charcoal-800 hover:border-charcoal-300 dark:hover:border-charcoal-600 transition-colors"
                    >
                        <SafeIcon icon={FiImage} />
                        Add Image/Video from Library
                    </button>
                    <div className="flex items-center gap-2 max-w-md">
                        <span className="text-xs text-charcoal-500 uppercase font-medium">OR</span>
                    </div>
                    <div className="flex items-center gap-2 max-w-md">
                        <input
                            type="url"
                            placeholder="Paste image/video URL here..."
                            className="shrink-0 flex-1 p-2 border border-charcoal-200 dark:border-charcoal-700 rounded-lg bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white text-sm focus:ring-2 focus:ring-charcoal-900 dark:focus:ring-white focus:border-transparent"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const val = e.target.value;
                                    if (val) {
                                        const isVideo = /\.(mp4|mov|webm)$/i.test(val);
                                        setMedia({ image_url: val, type: isVideo ? 'video' : 'image' });
                                        e.target.value = '';
                                    }
                                }
                            }}
                            onBlur={(e) => {
                                const val = e.target.value;
                                if (val) {
                                    const isVideo = /\.(mp4|mov|webm)$/i.test(val);
                                    setMedia({ image_url: val, type: isVideo ? 'video' : 'image' });
                                    e.target.value = '';
                                }
                            }}
                        />
                    </div>
                </div>
            )}
          </div>

          {/* Schedule (Optional) */}
          <div>
              <label className="block text-sm font-medium text-charcoal-700 dark:text-charcoal-300 mb-2">Schedule (Optional)</label>
              <input
                  type="datetime-local"
                  className="w-full p-2 border border-charcoal-200 dark:border-charcoal-700 bg-white dark:bg-charcoal-800 text-charcoal-900 dark:text-white rounded-lg focus:ring-2 focus:ring-charcoal-900 dark:focus:ring-white focus:border-transparent"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
              />
              <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-1">Leave blank to post immediately</p>
          </div>
        </div>

        {/* Footer - Added flex-shrink-0 to prevent compression */}
        <div className="flex-shrink-0 p-4 bg-charcoal-50 dark:bg-charcoal-800 border-t border-charcoal-100 dark:border-charcoal-700 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-charcoal-700 dark:text-charcoal-300 hover:text-charcoal-900 dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          {eventId && (
            <button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-2"
            >
              <SafeIcon icon={FiTrash2} />
              Delete
            </button>
          )}

          <div className="flex gap-2">
            <button
                onClick={() => handleSubmit('scheduled')}
                disabled={isSubmitting || !scheduledDate}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${
                    !scheduledDate
                    ? 'bg-charcoal-100 dark:bg-charcoal-700 text-charcoal-400 dark:text-charcoal-500 cursor-not-allowed'
                    : 'bg-white dark:bg-charcoal-700 border border-charcoal-300 dark:border-charcoal-600 text-charcoal-700 dark:text-charcoal-200 hover:bg-charcoal-50 dark:hover:bg-charcoal-600'
                }`}
            >
                <SafeIcon icon={FiClock} />
                Schedule
            </button>
            <button
                onClick={() => handleSubmit('published')}
                disabled={isSubmitting}
                className="px-6 py-2 bg-charcoal-900 dark:bg-white text-white dark:text-charcoal-900 rounded-lg hover:bg-charcoal-800 dark:hover:bg-charcoal-100 transition-colors flex items-center gap-2 text-sm font-medium shadow-sm"
            >
                {isSubmitting ? (
                    <span className="w-4 h-4 border-2 border-white/30 dark:border-charcoal-900/30 border-t-white dark:border-t-charcoal-900 rounded-full animate-spin"></span>
                ) : (
                    <SafeIcon icon={FiSend} />
                )}
                Post Now
            </button>
          </div>
        </div>
      </div>

      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={(asset) => setMedia(asset)}
      />
    </div>
  );
};

export default QuickPostModal;
